import { concat, toHex } from '../bytes/encoding';
import { ParseError } from '../bytes/errors';
import { importSpki, type PublicKey } from '../crypto/sig';
import { leafHash } from '../merkle/hash';
import { buildConsistencyProof, buildInclusionProof, rangeHash, TILE_WIDTH, tilePath, tileWidth, type TileCoord } from '../merkle/tile';
import type { ConsistencyProof, GetEntriesOptions, InclusionProof, InclusionTarget, LogClient, LogDescriptor, LogEntry, VerifiedTreeHead } from '../log/client';
import { parseCheckpoint, verifyCheckpoint } from '../checkpoint/checkpoint';
import { sctFromJson } from '../rfc6962/sct';
import { CT_V1, MerkleLeafType, type Sct } from '../rfc6962/types';
import { TransportError } from '../transport/errors';
import type { LogTransport } from '../transport/logTransport';
import { parseDataTile } from './datatile';
import { TileNodeSource } from './prover';
import { TileCache } from './tilecache';

const LEAF_PREFIX = new Uint8Array([CT_V1, MerkleLeafType.timestamped_entry]);

/** Client for a static-ct-api (c2sp.org/static-ct-api) log, e.g. Sunlight. Everything is derived from tiles. */
export class TiledClient implements LogClient {
  readonly kind = 'tiled' as const;
  readonly pageSize = TILE_WIDTH;
  readonly supportsHashLookup = false;
  private keyPromise?: Promise<PublicKey>;
  private lastTreeSize?: number;

  constructor(
    readonly log: LogDescriptor,
    private readonly transport: LogTransport,
    private readonly cache: TileCache = new TileCache(),
  ) {}

  private key(): Promise<PublicKey> | undefined {
    if (!this.log.key) return undefined;
    this.keyPromise ??= importSpki(this.log.key);
    return this.keyPromise;
  }

  private mon(path: string): string {
    return this.log.monitoringUrl + path;
  }

  async getTreeHead(signal?: AbortSignal): Promise<VerifiedTreeHead> {
    const init = signal ? { signal } : undefined;
    const { text } = await this.transport.text(this.log, 'tree-head', this.mon('checkpoint'), init);
    const cp = parseCheckpoint(text);
    const key = this.key();
    const out: VerifiedTreeHead = {
      treeSize: cp.treeSize,
      rootHash: cp.rootHash,
      signature: { status: 'no-key' },
      raw: text,
      origin: cp.origin,
      fetchedAt: Date.now(),
    };
    if (key && this.log.logId) {
      const v = await verifyCheckpoint(cp, this.log.logId, await key);
      out.signature = v.ok ? { status: 'verified' } : { status: 'failed', error: v.error ?? 'signature mismatch' };
      if (v.timestamp !== undefined) out.timestamp = v.timestamp;
    }
    this.lastTreeSize = cp.treeSize;
    return out;
  }

  private async treeSizeOr(given: number | undefined, signal?: AbortSignal): Promise<number> {
    if (given !== undefined) return given;
    if (this.lastTreeSize !== undefined) return this.lastTreeSize;
    return (await this.getTreeHead(signal)).treeSize;
  }

  /**
   * Fetches a hash or data tile. Partial tiles (`.p/W`) are tried first when the tree does not
   * fill the tile; logs may delete them once the full tile exists, so a 404 falls back to the full one.
   */
  async fetchTile(level: number | 'data', index: number, treeSize: number, signal?: AbortSignal): Promise<Uint8Array> {
    const width = tileWidth(level === 'data' ? 0 : level, index, treeSize);
    if (width === 0) throw new RangeError(`tile ${level}/${index} does not exist in a tree of ${treeSize} leaves`);
    const cls = level === 'data' ? 'entries' : 'tile';
    const init = signal ? { signal } : undefined;
    const fullKey = `${this.log.monitoringUrl}|${tilePath(level, index)}`;
    const cached = await this.cache.get(fullKey);
    if (cached) return cached;
    if (width < TILE_WIDTH) {
      const partialKey = `${fullKey}.p/${width}`;
      const c = await this.cache.get(partialKey);
      if (c) return c;
      try {
        const res = await this.transport.request(this.log, cls, this.mon(tilePath(level, index, width)), init);
        await this.cache.set(partialKey, res.body, false);
        return res.body;
      } catch (e) {
        if (!(e instanceof TransportError && e.kind === 'http' && e.status === 404)) throw e;
        // fall through to the full tile
      }
    }
    const res = await this.transport.request(this.log, cls, this.mon(tilePath(level, index)), init);
    await this.cache.set(fullKey, res.body, true);
    return res.body;
  }

  async getEntries(start: number, count: number, opts: GetEntriesOptions = {}): Promise<LogEntry[]> {
    if (count <= 0) return [];
    const treeSize = await this.treeSizeOr(opts.treeSize, opts.signal);
    if (start >= treeSize) return [];
    const tileIndex = Math.floor(start / TILE_WIDTH);
    const offset = start % TILE_WIDTH;
    const bytes = await this.fetchTile('data', tileIndex, treeSize, opts.signal);
    const leaves = parseDataTile(bytes);
    const out: LogEntry[] = [];
    const end = Math.min(offset + count, leaves.length, treeSize - tileIndex * TILE_WIDTH);
    for (let i = offset; i < end; i++) {
      const l = leaves[i]!;
      const raw = concat(LEAF_PREFIX, l.timestampedEntry);
      const entry: LogEntry = {
        index: tileIndex * TILE_WIDTH + i,
        leaf: { version: CT_V1, leafType: MerkleLeafType.timestamped_entry, entry: l.entry, raw },
        leafHash: await leafHash(raw),
        entryType: l.entry.entryType,
        cert: l.entry.entryType === 'x509' ? l.entry.x509! : l.entry.precert!.tbs,
        chain: l.issuerFingerprints.map((fingerprint) => ({ fingerprint })),
      };
      if (l.precert) entry.precert = l.precert;
      if (l.entry.precert) entry.issuerKeyHash = l.entry.precert.issuerKeyHash;
      out.push(entry);
    }
    return out;
  }

  private source(treeSize: number, signal?: AbortSignal): TileNodeSource {
    return new TileNodeSource((t: TileCoord) => this.fetchTile(t.level, t.index, treeSize, signal), treeSize);
  }

  async getInclusionProof(target: InclusionTarget, treeSize: number, signal?: AbortSignal): Promise<InclusionProof> {
    if (!('index' in target)) {
      throw new Error('Tiled logs have no lookup by leaf hash. Use the entry index (SCTs from these logs carry it in the leaf_index extension).');
    }
    if (target.index >= treeSize) throw new RangeError(`index ${target.index} is not below tree size ${treeSize}`);
    const [auditPath, entries] = await Promise.all([
      buildInclusionProof(target.index, treeSize, this.source(treeSize, signal)),
      this.getEntries(target.index, 1, { treeSize, ...(signal ? { signal } : {}) }),
    ]);
    const e = entries[0];
    const proof: InclusionProof = { leafIndex: target.index, treeSize, auditPath };
    if (e) {
      proof.leaf = e.leaf.raw;
      proof.leafHash = e.leafHash;
    }
    return proof;
  }

  async getConsistency(first: number, second: number, signal?: AbortSignal): Promise<ConsistencyProof> {
    const proof = await buildConsistencyProof(first, second, this.source(second, signal));
    return { first, second, proof };
  }

  /** Recomputes the root from the right-edge tiles; equal to the checkpoint root iff the tiles are consistent. */
  async computeRoot(treeSize: number, signal?: AbortSignal): Promise<Uint8Array> {
    return rangeHash(0, treeSize, this.source(treeSize, signal));
  }

  async getRoots(signal?: AbortSignal): Promise<Uint8Array[]> {
    const init = signal ? { signal } : undefined;
    const { value } = await this.transport.json<{ certificates: string[] }>(this.log, 'roots', this.log.submissionUrl + 'ct/v1/get-roots', init);
    if (!Array.isArray(value?.certificates)) throw new ParseError('get-roots: malformed response');
    return value.certificates.map((c) => Uint8Array.fromBase64(c));
  }

  async addChain(chain: Uint8Array[], precert: boolean, signal?: AbortSignal): Promise<Sct> {
    const url = this.log.submissionUrl + 'ct/v1/' + (precert ? 'add-pre-chain' : 'add-chain');
    const { value } = await this.transport.json(this.log, 'submit', url, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ chain: chain.map((c) => c.toBase64()) }),
      ...(signal ? { signal } : {}),
    });
    return sctFromJson(value);
  }

  async resolveIssuer(fingerprint: Uint8Array, signal?: AbortSignal): Promise<Uint8Array> {
    const hex = toHex(fingerprint);
    const key = `${this.log.monitoringUrl}|issuer/${hex}`;
    const cached = await this.cache.get(key);
    if (cached) return cached;
    const init = signal ? { signal } : undefined;
    const res = await this.transport.request(this.log, 'issuer', this.mon(`issuer/${hex}`), init);
    await this.cache.set(key, res.body, true);
    return res.body;
  }
}
